import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import Joyride, {
  ACTIONS,
  type CallBackProps,
  EVENTS,
  STATUS,
} from 'react-joyride';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { useFertWorkSessionPolling } from '@/app/hooks/useFertWorkSessionPolling';
import {
  useCreateFertWorkSessionMutation,
  useDeleteSessionMutation,
  useGenerateIncubatorMapsMutation,
  useGenerateLabelsMutation,
  useGenerateRunsMutation,
  useGetSessionDetailQuery,
  useRefreshSessionMutation,
  useSyncSessionMutation,
} from '@/app/services/fertWork';
import { PageStatusBanner } from '@/components/common';
import '@/components/guided-tour/tour.css';
import { TourHelpButton } from '@/components/guided-tour/TourHelpButton';
import { TourTooltip } from '@/components/guided-tour/TourTooltip';
import useLocalStorage from '@/components/hooks/useLocalStorage';
import { Button } from '@/components/ui/button';
import { ConfirmProvider } from '@/components/ui/confirm-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatUtcDateAsLocal } from '@/pages/fert-work/utils/dateHelpers';
import { setStoredIncubatorSelection } from '@/pages/fert-work/utils/incubatorSelection';
import { STATUS_CODES } from '@/pages/fert-work/utils/statusHelpers';
import { hasPermission, MODULES } from '@/services/auth';
import { useToast } from '@/services/toast';

import { unifiedSessionSteps } from '../../tours/unifiedSessionTour';
import { GenerateIncubatorMaps } from '../GenerateIncubatorMaps';
import { GenerateLabels } from '../GenerateLabels';
import { GenerateRuns } from '../GenerateRuns';
import { GenerateSOFLabels } from '../GenerateSOFLabels';
import { useRegenerateConfirm } from '../RegenerateConfirm';
import { ValidationHeader } from '../ValidationHeader';
import { SessionSettingsDrawer } from './SessionSettingsDrawer';
import { SessionTab } from './SessionTab';
import { FertWorkTab, TabBar } from './TabBar';

import '../../fertWorkTypography.css';

export type FertWorkSessionProps = {
  className?: string;
};

const FertWorkSessionContent = ({ className = '' }: FertWorkSessionProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const showRegenerateConfirm = useRegenerateConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const { me } = useAppSelector((state) => state.authentication);
  const sessionId = searchParams.get('sessionId') || 'fp1-2';
  const aspirationDate = searchParams.get('date') || 'Dec 10, 2025';
  const activeTab = (searchParams.get('tab') as FertWorkTab) || 'session';

  const setActiveTab = (tab: FertWorkTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'session') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      return next;
    });
  };

  const { status, isPolling, resetPolling } =
    useFertWorkSessionPolling(sessionId);

  const hasWritePermission = hasPermission(me, MODULES.FERT_WORK_WRITE);

  const shouldLoadDetail =
    status?.status === STATUS_CODES.ISSUES ||
    status?.status === STATUS_CODES.IN_PROGRESS ||
    status?.status === STATUS_CODES.COMPLETE ||
    status?.status === STATUS_CODES.ERROR ||
    status?.status === STATUS_CODES.NO_DATA;

  const { data: sessionDetail, isLoading: isLoadingDetail } =
    useGetSessionDetailQuery(sessionId, {
      skip: !shouldLoadDetail,
      refetchOnMountOrArgChange: true,
    });

  const [deleteSession, { isLoading: isDeleting }] = useDeleteSessionMutation();
  const [createSession, { isLoading: isCreating }] =
    useCreateFertWorkSessionMutation();
  const [refreshSession, { isLoading: isRefreshingSession }] =
    useRefreshSessionMutation();
  const [syncSession, { isLoading: isSyncing }] = useSyncSessionMutation();
  const [generateRuns, { isLoading: isGeneratingRuns }] =
    useGenerateRunsMutation();
  const [generateLabels, { isLoading: isGeneratingLabels }] =
    useGenerateLabelsMutation();
  const [generateMaps, { isLoading: isGeneratingMaps }] =
    useGenerateIncubatorMapsMutation();

  const isRefreshing = isDeleting || isCreating || isRefreshingSession;
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleGenerateRuns = useCallback(
    async (matTimeWindowHours: number) => {
      localStorage.setItem(
        'fertwork-mat-time-window',
        String(matTimeWindowHours),
      );
      try {
        await generateRuns({ sessionId, matTimeWindowHours }).unwrap();
        await generateLabels({ sessionId }).unwrap();
      } catch (err) {
        console.error('Failed to generate runs/labels:', err);
        toast.show({
          severity: 'error',
          summary: t('fertWork.toasts.generationFailed', 'Generation Failed'),
          detail: t(
            'fertWork.toasts.generationFailedRuns',
            'Failed to generate runs or labels. Please try again.',
          ),
          life: 5000,
        });
      }
    },
    [sessionId, generateRuns, generateLabels],
  );

  const handleGenerateMaps = useCallback(
    async (
      incubatorCounts: Record<string, number>,
      preferenceOrder: string[],
    ) => {
      setStoredIncubatorSelection({ incubatorCounts, preferenceOrder });
      try {
        await generateMaps({
          sessionId,
          incubatorCounts,
          preferenceOrder,
        }).unwrap();
      } catch (err) {
        console.error('Failed to generate maps:', err);
        toast.show({
          severity: 'error',
          summary: t('fertWork.toasts.generationFailed', 'Generation Failed'),
          detail: t(
            'fertWork.toasts.generationFailedMaps',
            'Failed to generate incubator maps. Please try again.',
          ),
          life: 5000,
        });
      }
    },
    [sessionId, generateMaps],
  );

  const handleRunsChanged = useCallback(async () => {
    try {
      await generateLabels({ sessionId }).unwrap();
    } catch (err) {
      console.error('Failed to regenerate labels after runs changed:', err);
    }
  }, [sessionId, generateLabels]);

  const handleRefreshSession = () => {
    if (!sessionDetail?.session) return;

    showRegenerateConfirm({
      header: t('fertWork.refreshSessionData', 'Refresh Session Data'),
      description: t(
        'fertWork.refreshDescription',
        'This will refresh the session data from the source system.',
      ),
      warningTitle: t('fertWork.dataWillBeReset', 'Data will be reset'),
      warningDetail: (
        <ul className="mt-1 ml-6 list-disc">
          <li>{t('fertWork.generatedRunsItem', 'Generated runs')}</li>
          <li>{t('fertWork.labelsItem', 'Labels')}</li>
          <li>{t('fertWork.incubatorMapsItem', 'Incubator maps')}</li>
        </ul>
      ),
      acceptLabel: t('fertWork.refresh', 'Refresh'),
      onAccept: async () => {
        try {
          await refreshSession(sessionId).unwrap();
          resetPolling();
        } catch (error) {
          console.error('Failed to refresh session:', error);
          toast.show({
            severity: 'error',
            summary: t('fertWork.toasts.refreshFailed', 'Refresh Failed'),
            detail: t(
              'fertWork.toasts.refreshFailedDetail',
              'Failed to refresh session data. Please try again.',
            ),
            life: 5000,
          });
        }
      },
    });
  };

  const handleSyncSession = async () => {
    try {
      const result = await syncSession(sessionId).unwrap();
      if (result.newRecordCount > 0) {
        toast.show({
          severity: 'success',
          summary: t('fertWork.toasts.syncComplete', 'Sync Complete'),
          detail: `${result.newRecordCount} new record(s) added. Regenerate runs to include them.`,
          life: 8000,
        });
      } else {
        toast.show({
          severity: 'info',
          summary: t('fertWork.toasts.syncComplete', 'Sync Complete'),
          detail: result.message || 'No new records found.',
          life: 5000,
        });
      }
    } catch (error) {
      console.error('Failed to sync session:', error);
      toast.show({
        severity: 'error',
        summary: t('common.error', 'Error'),
        detail: 'Failed to sync session. Please try again.',
        life: 5000,
      });
    }
  };

  const displayLocation = sessionDetail?.session?.siteName || 'Unknown Site';
  const displayDate =
    formatUtcDateAsLocal(sessionDetail?.session?.aspirationDate) ||
    aspirationDate;

  const isLoading =
    isPolling ||
    status?.status === STATUS_CODES.VALIDATING ||
    (shouldLoadDetail && isLoadingDetail);
  const hasSessionData = !!sessionDetail;
  const isReady =
    !isLoading &&
    status?.status !== STATUS_CODES.ERROR &&
    status?.status !== STATUS_CODES.NO_DATA &&
    hasSessionData;

  const [tourCompleted, setTourCompleted] = useLocalStorage(
    'tour-completed-fert-work-session-detail',
    false,
  );
  const [tourRun, setTourRun] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [touredTabs, setTouredTabs] = useLocalStorage(
    'tour-tabs-fert-work-session-detail',
    [] as string[],
  );
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  const runCount = sessionDetail?.session?.runCount ?? 0;
  const labelCount = sessionDetail?.session?.labelCount ?? 0;
  const mapCount = sessionDetail?.session?.incubatorMapCount ?? 0;

  const availableTourTabs = useMemo(() => {
    const tabs = new Set<string>(['session']);
    if (runCount > 0) tabs.add('runs');
    if (labelCount > 0) tabs.add('labels');
    if (mapCount > 0) {
      tabs.add('maps');
      tabs.add('sof-labels');
    }
    return tabs;
  }, [runCount, labelCount, mapCount]);

  const filteredTourSteps = useMemo(
    () =>
      unifiedSessionSteps.filter((step) => {
        const tab = (step.data as any)?.tab || 'session';
        return availableTourTabs.has(tab);
      }),
    [availableTourTabs],
  );

  useEffect(() => {
    if (
      isReady &&
      !tourCompleted &&
      filteredTourSteps.length > 0 &&
      !tourRun &&
      !touredTabs.includes(activeTab)
    ) {
      const firstStepForTab = filteredTourSteps.findIndex(
        (step) => ((step.data as any)?.tab || 'session') === activeTab,
      );
      if (firstStepForTab >= 0) {
        setTourStepIndex(firstStepForTab);
        const timer = setTimeout(() => setTourRun(true), 500);
        return () => clearTimeout(timer);
      }
    }
  }, [
    isReady,
    activeTab,
    tourCompleted,
    filteredTourSteps,
    tourRun,
    touredTabs,
  ]);

  useEffect(() => {
    const clearScroll = () => {
      document.body.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow');
      const main = document.querySelector('main');
      if (main && main.style.overflow) {
        main.style.removeProperty('overflow');
      }
    };
    if (!tourRun) {
      clearScroll();
      const timer = setTimeout(clearScroll, 100);
      return () => clearTimeout(timer);
    }
    return clearScroll;
  }, [tourRun]);

  const startTour = useCallback(() => {
    setTouredTabs([]);
    setActiveTab('session');
    setTourStepIndex(0);
    setTimeout(() => setTourRun(true), 300);
  }, [setActiveTab]);

  const markTabToured = useCallback((tab: string) => {
    setTouredTabs((prev: string[]) =>
      prev.includes(tab) ? prev : [...prev, tab],
    );
  }, []);

  const ALL_TABS: FertWorkTab[] = [
    'session',
    'runs',
    'labels',
    'maps',
    'sof-labels',
  ];

  useEffect(() => {
    if (!tourCompleted && ALL_TABS.every((tab) => touredTabs.includes(tab))) {
      setTourCompleted(true);
    }
  }, [touredTabs, tourCompleted, setTourCompleted]);

  const handleTourCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, type, status } = data;
      const currentTab =
        (filteredTourSteps[index]?.data as any)?.tab || 'session';

      if (
        status === STATUS.FINISHED ||
        status === STATUS.SKIPPED ||
        action === ACTIONS.CLOSE
      ) {
        setTourRun(false);
        setTourStepIndex(0);
        markTabToured(currentTab);
        setTourCompleted(true);
        return;
      }

      if (type === EVENTS.STEP_AFTER) {
        const nextIndex =
          action === ACTIONS.NEXT
            ? index + 1
            : action === ACTIONS.PREV
              ? index - 1
              : index;
        if (nextIndex < 0 || nextIndex >= filteredTourSteps.length) {
          setTourRun(false);
          setTourStepIndex(0);
          markTabToured(currentTab);
          return;
        }
        const nextTab =
          (filteredTourSteps[nextIndex].data as any)?.tab || 'session';
        if (nextTab !== currentTab) {
          setTourRun(false);
          setTourStepIndex(0);
          markTabToured(currentTab);
        } else {
          setTourStepIndex(nextIndex);
        }
        return;
      }

      if (type === EVENTS.TARGET_NOT_FOUND) {
        if (index + 1 < filteredTourSteps.length) {
          const nextTab =
            (filteredTourSteps[index + 1].data as any)?.tab || 'session';
          if (nextTab !== currentTab) {
            setTourRun(false);
            setTourStepIndex(0);
            markTabToured(currentTab);
          } else {
            setTourStepIndex(index + 1);
          }
        } else {
          setTourRun(false);
          setTourStepIndex(0);
          markTabToured(currentTab);
        }
      }
    },
    [setTourCompleted, filteredTourSteps, markTabToured],
  );

  return (
    <div
      className={`fert-work-session-page relative flex flex-auto flex-col ${className}`}
    >
      {tourRun && (
        <Joyride
          steps={filteredTourSteps}
          run={tourRun}
          stepIndex={tourStepIndex}
          callback={handleTourCallback}
          continuous
          showSkipButton
          spotlightClicks
          disableScrolling
          disableScrollParentFix
          tooltipComponent={TourTooltip}
          styles={{ options: { zIndex: 10000 } }}
        />
      )}
      <SessionSettingsDrawer
        visible={settingsOpen}
        onHide={() => setSettingsOpen(false)}
      />
      <div className="flex flex-col gap-4 p-4">
        <div data-tour="session-header">
          <ValidationHeader
            sessionId={sessionId}
            location={displayLocation}
            date={displayDate}
            headerExtra={
              <div className="flex items-center gap-1">
                {hasWritePermission && isReady && (
                  <>
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <Button
                            variant="outline"
                            size="icon-sm"
                            onClick={handleSyncSession}
                            disabled={isSyncing || isRefreshing}
                            className="flex-shrink-0 text-sky-700"
                          >
                            <i
                              className={
                                isSyncing
                                  ? 'pi pi-spin pi-spinner'
                                  : 'pi pi-sync'
                              }
                            />
                            <span className="sr-only">
                              {t(
                                'fertWork.syncSessionTooltip',
                                'Check for new records',
                              )}
                            </span>
                          </Button>
                        }
                      />
                      <TooltipContent side="left">
                        {t(
                          'fertWork.syncSessionTooltip',
                          'Check for new records',
                        )}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <Button
                            variant="outline"
                            size="icon-sm"
                            onClick={handleRefreshSession}
                            disabled={isRefreshing || isSyncing}
                            className="flex-shrink-0"
                          >
                            <i
                              className={
                                isRefreshing
                                  ? 'pi pi-spin pi-spinner'
                                  : 'pi pi-refresh'
                              }
                            />
                            <span className="sr-only">
                              {t(
                                'fertWork.refreshSessionDataTooltip',
                                'Refresh session data',
                              )}
                            </span>
                          </Button>
                        }
                      />
                      <TooltipContent side="left">
                        {t(
                          'fertWork.refreshSessionDataTooltip',
                          'Refresh session data',
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </>
                )}
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={() => setSettingsOpen(true)}
                        className="flex-shrink-0"
                      >
                        <i className="pi pi-cog" />
                        <span className="sr-only">
                          {t('fertWork.sessionSettings', 'Session Settings')}
                        </span>
                      </Button>
                    }
                  />
                  <TooltipContent side="left">
                    {t('fertWork.sessionSettings', 'Session Settings')}
                  </TooltipContent>
                </Tooltip>
                <TourHelpButton onClick={startTour} />
              </div>
            }
          />
        </div>

        {isLoading && (
          <div className="rounded-md bg-blue-50 p-4 text-blue-700">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-medium">
                {t(
                  'fertWork.processingSessionProgress',
                  'Processing session...',
                )}
              </span>
              <span className="text-sm font-semibold">
                {status?.progressPercentage?.toFixed(0) || 0}%
              </span>
            </div>
            <div className="mb-2 h-2.5 w-full rounded-full bg-blue-200">
              <div
                className="h-2.5 rounded-full bg-blue-600 transition-all duration-300 ease-out"
                style={{
                  width: `${status?.progressPercentage?.toFixed(0) || 0}%`,
                }}
              ></div>
            </div>
            {status?.processedRecordCount && status?.totalRecordsToProcess && (
              <div className="text-sm">
                {t(
                  'fertWork.processedRecords',
                  'Processed {processed} of {total} records',
                  {
                    processed: status.processedRecordCount,
                    total: status.totalRecordsToProcess,
                  },
                )}
              </div>
            )}
          </div>
        )}

        {!isLoading &&
          !hasSessionData &&
          status?.status !== STATUS_CODES.ERROR && (
            <PageStatusBanner
              type="loading"
              message={t(
                'fertWork.loadingSessionData',
                'Loading session data...',
              )}
            />
          )}

        {status?.status === STATUS_CODES.NO_DATA && (
          <PageStatusBanner
            type="empty"
            message={t(
              'fertWork.noMatingRecords',
              'No mating records scheduled for {location} on {date}.',
              {
                location: displayLocation,
                date: displayDate,
              },
            )}
          />
        )}

        {status?.status === STATUS_CODES.ERROR && (
          <PageStatusBanner
            type="error"
            message={t(
              'fertWork.errorProcessingSession',
              'Error processing session: {error}',
              {
                error: status.errorMessage,
              },
            )}
          />
        )}

        {isReady && (
          <>
            {sessionDetail?.analytics?.hasPendingEscalations && (
              <div className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
                <i className="pi pi-flag mt-0.5 flex-shrink-0 text-orange-500" />
                <span>
                  <strong>
                    {t(
                      'fertWork.waitingForConfirmation',
                      'Waiting for confirmation',
                    )}
                  </strong>{' '}
                  —{' '}
                  {t(
                    'fertWork.escalatedIssuesMustBeResolved',
                    '{count} escalated issue(s) must be resolved before generating runs, labels, or maps.',
                    {
                      count: sessionDetail.analytics.escalatedIssueCount,
                    },
                  )}
                </span>
              </div>
            )}

            <TabBar
              activeTab={activeTab}
              onTabChange={setActiveTab}
              runCount={runCount}
              invalidRunCount={sessionDetail?.session?.invalidRunCount ?? 0}
              warningRunCount={sessionDetail?.session?.warningRunCount ?? 0}
              labelCount={labelCount}
              incubatorMapCount={mapCount}
              sofLabelCount={labelCount}
              sessionId={sessionId}
              onGenerateRuns={handleGenerateRuns}
              onGenerateMaps={handleGenerateMaps}
              isGeneratingRuns={isGeneratingRuns || isGeneratingLabels}
              isGeneratingMaps={isGeneratingMaps}
            />

            {activeTab === 'session' && (
              <SessionTab sessionId={sessionId} sessionDetail={sessionDetail} />
            )}

            {activeTab === 'runs' &&
              (isGeneratingRuns ? (
                <PageStatusBanner
                  type="loading"
                  message={t('fertWork.generatingRuns', 'Generating runs...')}
                />
              ) : (
                <GenerateRuns
                  sessionId={sessionId}
                  onRunsChanged={handleRunsChanged}
                />
              ))}

            {activeTab === 'labels' &&
              (isGeneratingLabels ? (
                <PageStatusBanner
                  type="loading"
                  message={t(
                    'fertWork.generatingLabels',
                    'Generating labels...',
                  )}
                />
              ) : (
                <GenerateLabels
                  sessionId={sessionId}
                  isCommercial={sessionDetail?.sessionType === 1}
                />
              ))}

            {activeTab === 'maps' &&
              (isGeneratingMaps ? (
                <PageStatusBanner
                  type="loading"
                  message={t(
                    'fertWork.generatingIncubatorMaps',
                    'Generating incubator maps...',
                  )}
                />
              ) : (
                <GenerateIncubatorMaps sessionId={sessionId} />
              ))}

            {activeTab === 'sof-labels' &&
              (isGeneratingMaps ? (
                <PageStatusBanner
                  type="loading"
                  message={t(
                    'fertWork.generatingIncubatorMaps',
                    'Generating incubator maps...',
                  )}
                />
              ) : (
                <GenerateSOFLabels sessionId={sessionId} />
              ))}
          </>
        )}
      </div>
    </div>
  );
};

export const FertWorkSession = (props: FertWorkSessionProps) => (
  <ConfirmProvider>
    <FertWorkSessionContent {...props} />
  </ConfirmProvider>
);
